import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation, AnimatePresence, useInView } from 'framer-motion';
import { 
  ChevronDownIcon, 
  CursorClickIcon, 
  LightningBoltIcon, 
  ChartBarIcon, 
  CashIcon, 
  LocationMarkerIcon,
  MenuIcon,
  XIcon,
  CheckCircleIcon,
  UserGroupIcon,
  CubeTransparentIcon,
  ShieldCheckIcon,
  BadgeCheckIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  EyeIcon,
  PhotographIcon,
  ClockIcon,
  ArrowRightIcon,
  PlusIcon,
  ChevronRightIcon,
  SearchIcon,
  CalendarIcon,
  ChartPieIcon,
  CreditCardIcon,
  UserIcon,
  DeviceMobileIcon,
  GlobeAltIcon
} from '@heroicons/react/outline';

// Composant principal de la Landing Page
const LandingPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [navbarOpen, setNavbarOpen] = useState(false);
  const [isNavTransparent, setIsNavTransparent] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  
  // Références pour l'animation au scroll
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const featuresRef = useRef(null);
  const platformRef = useRef(null);
  const screensRef = useRef(null);
  const partnersRef = useRef(null);
  const testimonialsRef = useRef(null);
  
  // InView Hooks pour déclencher les animations
  const isHeroInView = useInView(heroRef, { once: false });
  const isStatsInView = useInView(statsRef, { once: false });
  const isFeaturesInView = useInView(featuresRef, { once: false });
  const isPlatformInView = useInView(platformRef, { once: false });
  const isScreensInView = useInView(screensRef, { once: false });
  const isPartnersInView = useInView(partnersRef, { once: false });
  const isTestimonialsInView = useInView(testimonialsRef, { once: false });
  
  // Animations contrôlées
  const heroControls = useAnimation();
  const statsControls = useAnimation();
  const featuresControls = useAnimation();
  const platformControls = useAnimation();
  const screensControls = useAnimation();
  const partnersControls = useAnimation();
  const testimonialsControls = useAnimation();
  
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setIsNavTransparent(window.scrollY < 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Timer pour slideshow de la Hero Section
    const slideshowTimer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % 3);
    }, 5000);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(slideshowTimer);
    };
  }, []);
  
  // Effet pour les animations au scroll
  useEffect(() => {
    if (isHeroInView) heroControls.start("visible");
    if (isStatsInView) statsControls.start("visible");
    if (isFeaturesInView) featuresControls.start("visible");
    if (isPlatformInView) platformControls.start("visible");
    if (isScreensInView) screensControls.start("visible");
    if (isPartnersInView) partnersControls.start("visible");
    if (isTestimonialsInView) testimonialsControls.start("visible");
  }, [
    isHeroInView, isStatsInView, isFeaturesInView, isPlatformInView, 
    isScreensInView, isPartnersInView, isTestimonialsInView,
    heroControls, statsControls, featuresControls, platformControls, 
    screensControls, partnersControls, testimonialsControls
  ]);

  // Images du slideshow pour la Hero Section
  const slideshowImages = [
    {
      url: "https://images.unsplash.com/photo-1616588589676-62b3bd4ff6d2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      title: 'CAMPAGNES PERCUTANTES',
      description: 'Captez l\'attention dans les zones à fort trafic'
    },
    {
      url: "https://images.unsplash.com/photo-1517292987719-0369a794ec0f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2274&q=80",
      title: 'VISIBILITÉ MAXIMALE',
      description: 'Affichez votre message sur des écrans premium'
    },
    {
      url: "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2274&q=80",
      title: 'CEINTURE DIGITALE',
      description: 'Plus de 100 écrans stratégiquement placés'
    }
  ];

  // Effets d'animation Framer Motion
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.6 }
    }
  };
  
  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.7 }
    }
  };
  
  const slideInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.7 }
    }
  };

  return (
    <div className="bg-white min-h-screen overflow-x-hidden font-sans">
      {/* Navbar - Style redesigné avec effet de verre */}
      <motion.nav 
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
          isNavTransparent 
            ? 'bg-transparent' 
            : 'bg-white bg-opacity-85 backdrop-blur-lg shadow-xl'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <motion.div 
                className="flex-shrink-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="flex items-center">
                  <div className="h-10 w-10 bg-[#E84E0E] rounded-md flex items-center justify-center mr-2 shadow-lg">
                    <span className="text-white font-bold text-xl">A</span>
                  </div>
                  <div className={`font-bold text-xl ${isNavTransparent ? 'text-white' : 'text-gray-900'}`}>
                    ACCENT MEDIA
                  </div>
                </div>
              </motion.div>
              <div className="hidden md:block">
                <div className="ml-10 flex items-center space-x-8">
                  {['Accueil', 'Services', 'Plateforme', 'Écrans', 'Partenariat', 'Contact'].map((item, index) => (
                    <motion.a
                      key={item}
                      href={`#${item.toLowerCase()}`}
                      className={`${isNavTransparent ? 'text-white hover:text-[#E84E0E]' : 'text-gray-800 hover:text-[#E84E0E]'} transition-all duration-200 px-3 py-1 text-sm font-medium`}
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                      whileHover={{ y: -2 }}
                    >
                      {item}
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <motion.a
                href="#login"
                className={`${isNavTransparent ? 'text-white hover:text-[#E84E0E]' : 'text-gray-800 hover:text-[#E84E0E]'} font-medium transition-all duration-200`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                Se connecter
              </motion.a>
              <motion.button
                className={`${isNavTransparent ? 'bg-white text-[#E84E0E]' : 'bg-[#E84E0E] text-white'} hover:bg-opacity-90 font-bold py-2 px-5 rounded-full shadow-md transition-all duration-300`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(232, 78, 14, 0.4)" }}
                whileTap={{ scale: 0.95 }}
              >
                Espace client
              </motion.button>
            </div>
            <div className="-mr-2 flex md:hidden">
              <motion.button
                onClick={() => setNavbarOpen(!navbarOpen)}
                className={`inline-flex items-center justify-center p-2 rounded-md ${isNavTransparent ? 'text-white hover:text-[#E84E0E]' : 'text-gray-800 hover:text-[#E84E0E]'} transition-colors duration-200`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <span className="sr-only">Ouvrir le menu</span>
                {navbarOpen ? (
                  <XIcon className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                )}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Menu mobile avec animation améliorée */}
        <AnimatePresence>
          {navbarOpen && (
            <motion.div 
              className="md:hidden bg-white shadow-xl rounded-b-2xl"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <div className="px-4 pt-2 pb-6 space-y-2">
                {['Accueil', 'Services', 'Plateforme', 'Écrans', 'Partenariat', 'Contact'].map((item, i) => (
                  <motion.a
                    key={item}
                    href={`#${item.toLowerCase()}`}
                    className="block px-4 py-3 rounded-lg text-gray-800 hover:bg-orange-50 hover:text-[#E84E0E] transition-colors duration-200 font-medium"
                    onClick={() => setNavbarOpen(false)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    whileHover={{ x: 5 }}
                  >
                    {item}
                  </motion.a>
                ))}
                <div className="pt-4 pb-2 space-y-2">
                  <a href="#login" className="block px-4 py-3 text-[#E84E0E] font-medium">
                    Se connecter
                  </a>
                  <button className="w-full bg-[#E84E0E] text-white font-bold py-3 px-4 rounded-lg shadow-lg">
                    Espace client
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Hero Section redesignée - Plus moderne avec animations plus fluides */}
      <header 
        ref={heroRef}
        className="relative min-h-screen flex flex-col items-center overflow-hidden bg-gradient-to-br from-gray-900 to-black" 
        id="accueil"
      >
        {/* Éléments décoratifs modernes inspirés de Dribbble */}
        <div className="absolute inset-0 w-full h-full">
          {/* Fond avec dégradé et texture */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-black to-black opacity-90 z-0"></div>
          
          {/* Motifs géométriques animés */}
          <div className="absolute left-0 top-0 w-full h-full overflow-hidden">
            <motion.div 
              className="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#E84E0E] opacity-5 blur-3xl"
              animate={{ 
                scale: [1, 1.3, 1],
                x: [0, 50, 0],
                y: [0, 30, 0],
              }}
              transition={{ 
                duration: 15,
                repeat: Infinity,
                repeatType: "reverse",
              }}
            />
            <motion.div 
              className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-purple-600 opacity-5 blur-3xl"
              animate={{ 
                scale: [1, 1.2, 1],
                x: [0, -50, 0],
                y: [0, 50, 0],
              }}
              transition={{ 
                duration: 18,
                repeat: Infinity,
                repeatType: "reverse",
                delay: 2
              }}
            />
          </div>
          
          {/* Grille décorative */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zMCAzMGgxdjFoLTF6bTEgMGgxdjFoLTF6bS0yIDBoMXYxaC0xem0tMjkgMGgxdjFoLTF6bTMwLTFoMXYxaC0xeiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgZmlsbC1ydWxlPSJub256ZXJvIi8+PC9nPjwvc3ZnPg==')] bg-[length:40px_40px] opacity-10 z-10"></div>
        </div>

        {/* Contenu principal Hero avec layout moderne */}
        <div className="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 lg:pt-32 flex flex-col justify-center h-full">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
            initial="hidden"
            animate={heroControls}
            variants={staggerContainer}
          >
            {/* Texte principal avec typographie améliorée */}
            <motion.div variants={fadeInUp} className="text-center lg:text-left">
              <motion.div 
                className="inline-block bg-gradient-to-r from-[#E84E0E] to-orange-600 px-4 py-1 rounded-full mb-6 shadow-lg"
                whileHover={{ scale: 1.05 }}
              >
                <span className="text-sm font-semibold tracking-wider text-white">ACCENT MEDIA & TECHNOLOGY</span>
              </motion.div>
              
              <motion.h1 variants={fadeInUp} className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight tracking-tight text-white">
                <span className="block mb-1">Révolutionnez</span>
                <span className="block">votre <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#E84E0E] to-orange-500">affichage</span></span>
                <span className="block">publicitaire</span>
              </motion.h1>
              
              <motion.p variants={fadeInUp} className="text-lg md:text-xl text-gray-300 mb-10 max-w-lg mx-auto lg:mx-0">
                Notre plateforme intuitive vous permet de gérer et réserver des espaces publicitaires 
                sur notre réseau d'écrans digitaux à haute visibilité dans toute la ville.
              </motion.p>
              
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 justify-center lg:justify-start">
                <motion.button 
                  className="bg-gradient-to-r from-[#E84E0E] to-orange-600 hover:from-[#E84E0E] hover:to-[#E84E0E] text-white font-bold py-4 px-8 rounded-full shadow-xl transition duration-300 ease-in-out flex items-center justify-center"
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: "0 0 25px rgba(232, 78, 14, 0.6)" 
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Explorer la plateforme</span>
                  <ChevronRightIcon className="h-5 w-5 ml-2" />
                </motion.button>
                
                <motion.button 
                  className="bg-transparent text-white border-2 border-white hover:border-[#E84E0E] hover:bg-white hover:bg-opacity-5 font-bold py-4 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Demander une démo
                </motion.button>
              </motion.div>
              
              {/* Indicateurs de confiance */}
              <motion.div 
                variants={fadeInUp} 
                className="flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-4 sm:space-y-0 sm:space-x-8 mt-10 text-gray-300"
              >
                <div className="flex items-center">
                  <div className="flex -space-x-2 mr-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-8 w-8 rounded-full border-2 border-white bg-gray-800 flex items-center justify-center text-xs font-medium">
                        {["JD", "MS", "AL"][i]}
                      </div>
                    ))}
                  </div>
                  <span className="text-sm">Rejoint par 200+ annonceurs</span>
                </div>
                <div className="flex items-center">
                  <div className="flex items-center mr-1 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <span className="text-sm">Note de 4.9/5</span>
                </div>
              </motion.div>
            </motion.div>
            
            {/* Écran mockup avec design moderne inspiré de Dribbble */}
            <motion.div 
              variants={slideInRight}
              className="relative h-[500px] lg:h-[600px] p-6"
            >
              <div className="absolute inset-0 flex items-center justify-center">
                {/* Points de connexion décoratifs */}
                <div className="absolute w-full h-full">
                  {[...Array(8)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-2 h-2 bg-[#E84E0E] rounded-full opacity-70"
                      style={{
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                      }}
                      animate={{ 
                        opacity: [0.3, 0.8, 0.3],
                        scale: [1, 1.3, 1]
                      }}
                      transition={{ 
                        duration: 2 + Math.random() * 3,
                        repeat: Infinity,
                        delay: Math.random() * 2
                      }}
                    />
                  ))}
                  
                  {/* Lignes de connexion animées */}
                  {[...Array(6)].map((_, i) => {
                    const startPos = { x: Math.random() * 100, y: Math.random() * 100 };
                    const endPos = { x: Math.random() * 100, y: Math.random() * 100 };
                    return (
                      <motion.div
                        key={`line-${i}`}
                        className="absolute h-px bg-gradient-to-r from-orange-500 to-transparent"
                        style={{
                          top: `${startPos.y}%`,
                          left: `${startPos.x}%`,
                          width: '100px',
                          transform: `rotate(${Math.random() * 360}deg)`,
                          opacity: 0.2
                        }}
                        animate={{ opacity: [0.1, 0.3, 0.1] }}
                        transition={{ 
                          duration: 3 + Math.random() * 4,
                          repeat: Infinity
                        }}
                      />
                    );
                  })}
                </div>
                
                {/* Mockup de l'écran */}
                <div className="relative w-full max-w-md md:max-w-lg">
                  {/* Cadre ornemental */}
                  <motion.div 
                    className="absolute -inset-3 rounded-xl bg-gradient-to-r from-[#E84E0E] via-orange-500 to-rose-500 opacity-70 blur-lg -z-10"
                    animate={{ opacity: [0.5, 0.8, 0.5] }}
                    transition={{ duration: 4, repeat: Infinity, repeatType: "reverse" }}
                  />
                  
                  {/* Cadre principal de l'écran */}
                  <div className="bg-black rounded-xl overflow-hidden shadow-2xl border border-gray-800 relative">
                    {/* Barre supérieure simulant un navigateur */}
                    <div className="bg-gray-900 p-3 flex items-center border-b border-gray-800">
                      <div className="flex space-x-2 mr-4">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      </div>
                      <div className="flex-1 bg-gray-800 rounded-md h-6 flex items-center justify-center">
                        <span className="text-gray-400 text-xs truncate px-2">accentmedia.com/platform</span>
                      </div>
                    </div>
                    
                    {/* Contenu de l'écran - Slideshow */}
                    <div className="h-[450px] relative overflow-hidden">
                      {slideshowImages.map((slide, index) => (
                        <motion.div
                          key={index}
                          className="absolute inset-0 w-full h-full"
                          initial={{ opacity: 0 }}
                          animate={{ 
                            opacity: activeSlide === index ? 1 : 0,
                          }}
                          transition={{ duration: 1.2, ease: "easeInOut" }}
                        >
                          {/* Image avec un overlay design */}
                          <div 
                            className="absolute inset-0 bg-cover bg-center"
                            style={{ backgroundImage: `url(${slide.url})` }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black opacity-70"></div>
                          
                          {/* Interface utilisateur superposée */}
                          <div className="absolute inset-0 flex flex-col justify-between p-8">
                            {/* UI: Barre de navigation fictive */}
                            <div className="flex items-center justify-between mb-8">
                              <div className="flex space-x-4 text-white text-opacity-80 text-sm">
                                <a href="#" className="hover:text-white transition">Carte</a>
                                <a href="#" className="hover:text-white transition">Écrans</a>
                                <a href="#" className="hover:text-white transition">Campaigns</a>
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="h-7 w-7 bg-white bg-opacity-10 rounded-full flex items-center justify-center">
                                  <SearchIcon className="h-3 w-3 text-white" />
                                </div>
                                <div className="h-7 w-7 bg-white bg-opacity-10 rounded-full flex items-center justify-center">
                                  <BellIcon className="h-3 w-3 text-white" />
                                </div>
                              </div>
                            </div>
                            
                            {/* UI: Panel informatif */}
                            <div>
                              <div className="bg-black bg-opacity-50 backdrop-blur-sm rounded-xl p-6 border border-white border-opacity-10">
                                <h3 className="text-white text-2xl font-bold mb-3">{slide.title}</h3>
                                <p className="text-white text-opacity-90 mb-4">{slide.description}</p>
                                <div className="flex space-x-4 mb-4">
                                  <div className="flex items-center space-x-1 text-white text-opacity-80">
                                    <EyeIcon className="h-4 w-4" />
                                    <span className="text-xs">24.5K vues/jour</span>
                                  </div>
                                  <div className="flex items-center space-x-1 text-white text-opacity-80">
                                    <LocationMarkerIcon className="h-4 w-4" />
                                    <span className="text-xs">15 emplacements</span>
                                  </div>
                                </div>
                                <button className="bg-[#E84E0E] text-white text-sm py-2 px-4 rounded-full flex items-center">
                                  <span>Réserver maintenant</span>
                                  <ArrowRightIcon className="h-3 w-3 ml-2" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Indicateurs du slideshow */}
                    <div className="absolute bottom-6 right-6 flex space-x-2">
                      {slideshowImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setActiveSlide(index)}
                          className={`h-2 w-${activeSlide === index ? '8' : '2'} rounded-full transition-all duration-300 ${
                            activeSlide === index ? 'bg-[#E84E0E]' : 'bg-white bg-opacity-30'
                          }`}
                          aria-label={`Slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Orbes décoratifs */}
              <motion.div 
                className="absolute top-0 right-1/4 h-16 w-16 rounded-full bg-gradient-to-br from-[#E84E0E] to-orange-500 opacity-80 blur-lg"
                animate={{ 
                  y: [0, -15, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{ 
                  duration: 4,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              />
              <motion.div 
                className="absolute bottom-10 right-10 h-24 w-24 rounded-full border-4 border-[#E84E0E] border-opacity-30"
                animate={{ 
                  rotate: 360,
                  scale: [1, 1.05, 1]
                }}
                transition={{ 
                  rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                  scale: { duration: 3, repeat: Infinity, repeatType: "reverse" }
                }}
              />
            </motion.div>
          </motion.div>
          
          {/* Indicateur de défilement stylé */}
          <motion.div 
            className="absolute bottom-10 left-0 right-0 flex justify-center z-30"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2, duration: 1 }}
          >
            <motion.a 
              href="#stats"
              className="flex flex-col items-center space-y-2 text-white text-opacity-80 hover:text-opacity-100 transition-colors"
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatType: "loop" }}
            >
              <span className="text-xs font-medium">Découvrir</span>
              <ChevronDownIcon className="h-5 w-5" />
            </motion.a>
          </motion.div>
        </div>
      </header>

      {/* Section Stats - Style inspiré des dashboards analytiques sur Dribbble */}
      <section 
        ref={statsRef}
        className="py-20 px-4 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden"
        id="stats"
      >
        {/* Motif décoratif de fond */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMwLTkuOTQtOC4wNi0xOC0xOC0xOFYwQzI2LjA4IDAgMzYgOS45NCAzNiAyMmg5LjAyYzAgMTAuMTMtOC4xMiAxOC4yNi0xOC4yNiAxOC4yNnYtOS4wMkMzMS44NCAzMS4yNCAzNiAyNy4wOCAzNiAxOHoiIGZpbGw9IiNFODRFMEUiLz48L2c+PC9zdmc+')] bg-[length:60px_60px]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            animate={statsControls}
            variants={fadeInUp}
          >
            <motion.div variants={scaleIn} className="inline-block bg-orange-100 text-[#E84E0E] text-sm font-semibold px-4 py-1 rounded-full mb-4">
              NOS CHIFFRES
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold mt-2 mb-4 text-gray-900">
              Plateforme leader de l'affichage digital
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-2xl mx-auto">
              Notre réseau d'écrans stratégiquement placés vous offre une couverture optimale pour maximiser l'impact de vos campagnes
            </motion.p>
          </motion.div>
          
          {/* Grille de stats avec design inspiré de dashboards analytiques */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            initial="hidden"
            animate={statsControls}
            variants={staggerContainer}
          >
            {[
              { value: "100+", label: "Écrans digitaux", icon: <CubeTransparentIcon className="h-8 w-8" /> },
              { value: "50k+", label: "Personnes touchées par jour", icon: <EyeIcon className="h-8 w-8" /> },
              { value: "15+", label: "Villes couvertes", icon: <LocationMarkerIcon className="h-8 w-8" /> },
              { value: "24/7", label: "Diffusion sans interruption", icon: <ClockIcon className="h-8 w-8" /> }
            ].map((stat, index) => (
              <motion.div 
                key={index} 
                className="relative bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden"
                variants={scaleIn}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.1)" }}
              >
                {/* Fond décoratif animé */}
                <motion.div 
                  className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-orange-100 opacity-80"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 10, 0],
                  }}
                  transition={{ 
                    duration: 5,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#E84E0E] to-orange-500 rounded-2xl shadow-lg flex items-center justify-center text-white mb-6">
                    {stat.icon}
                  </div>
                  <motion.p 
                    className="text-4xl md:text-5xl font-bold text-gray-900 mb-2"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
                  >
                    {stat.value}
                  </motion.p>
                  <p className="text-gray-600">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
          
          {/* Section de performance ajoutée - Inspirée des dashboards sur Dribbble */}
          <motion.div 
            className="mt-24 bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100"
            initial="hidden"
            animate={statsControls}
            variants={fadeInUp}
            transition={{ delay: 0.4 }}
          >
            <div className="p-8 bg-gray-50 border-b border-gray-100">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Performance de notre réseau</h3>
                  <p className="text-gray-600">Mesures d'audience et impact publicitaire moyens</p>
                </div>
                <div className="mt-4 md:mt-0">
                  <div className="inline-flex rounded-md shadow">
                    <button className="bg-[#E84E0E] text-white text-sm py-2 px-4 rounded-md flex items-center space-x-2">
                      <DownloadIcon className="h-4 w-4" />
                      <span>Télécharger le rapport</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Carte de performance 1 */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-700">Taux d'engagement</h4>
                    <div className="flex items-center text-green-500 font-medium text-sm">
                      <ArrowUpIcon className="h-4 w-4 mr-1" />
                      <span>+12.5%</span>
                    </div>
                  </div>
                  
                  <div className="relative h-28 mb-2">
                    {/* Chart simulé */}
                    <div className="absolute bottom-0 inset-x-0 h-full flex items-end space-x-2">
                      {[40, 65, 50, 80, 60, 75, 90, 70, 85].map((height, i) => (
                        <motion.div 
                          key={i}
                          className="flex-1 bg-gradient-to-t from-[#E84E0E] to-orange-400 rounded-t-sm"
                          initial={{ height: 0 }}
                          whileInView={{ height: `${height}%` }}
                          transition={{ duration: 1, delay: i * 0.05 }}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Jan</span>
                    <span>Fév</span>
                    <span>Mar</span>
                    <span>Avr</span>
                    <span>Mai</span>
                    <span>Jun</span>
                    <span>Jul</span>
                    <span>Aou</span>
                    <span>Sep</span>
                  </div>
                </div>
                
                {/* Carte de performance 2 */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="font-semibold text-gray-700">Répartition d'audience</h4>
                    <div className="text-xs text-gray-500">Dernier mois</div>
                  </div>
                  
                  <div className="flex justify-center mb-4">
                    <div className="relative h-36 w-36">
                      {/* Cercle de fond */}
                      <div className="absolute inset-0 rounded-full border-8 border-gray-100"></div>
                      
                      {/* Segments du donut chart */}
                      <motion.div 
                        className="absolute inset-0 rounded-full border-8 border-transparent border-t-[#E84E0E] border-r-[#E84E0E] border-b-[#E84E0E]"
                        initial={{ rotate: 0 }}
                        whileInView={{ rotate: 45 }}
                        transition={{ duration: 1, delay: 0.2 }}
                      />
                      <motion.div 
                        className="absolute inset-0 rounded-full border-8 border-transparent border-l-blue-400"
                        initial={{ rotate: 0 }}
                        whileInView={{ rotate: 45 }}
                        transition={{ duration: 1, delay: 0.2 }}
                      />
                      
                      {/* Centre du donut */}
                      <div className="absolute inset-4 rounded-full bg-white flex items-center justify-center">
                        <span className="text-xl font-bold text-gray-800">75%</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-center space-x-6">
                    <div className="flex items-center">
                      <div className="h-3 w-3 bg-[#E84E0E] rounded-full mr-2"></div>
                      <span className="text-sm text-gray-700">Zones urbaines</span>
                    </div>
                    <div className="flex items-center">
                      <div className="h-3 w-3 bg-blue-400 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-700">Périphéries</span>
                    </div>
                  </div>
                </div>
                
                {/* Carte de performance 3 */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="font-semibold text-gray-700">Conversion commerciale</h4>
                    <div className="flex items-center text-green-500 font-medium text-sm">
                      <ArrowUpIcon className="h-4 w-4 mr-1" />
                      <span>+8.2%</span>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-600">Taux de mémorisation</span>
                      <span className="text-sm font-medium text-gray-800">82%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-[#E84E0E] rounded-full"
                        initial={{ width: 0 }}
                        whileInView={{ width: '82%' }}
                        transition={{ duration: 1, delay: 0.3 }}
                      />
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-600">Intention d'achat</span>
                      <span className="text-sm font-medium text-gray-800">68%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-orange-400 rounded-full"
                        initial={{ width: 0 }}
                        whileInView={{ width: '68%' }}
                        transition={{ duration: 1, delay: 0.5 }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-600">Visite en magasin</span>
                      <span className="text-sm font-medium text-gray-800">45%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-blue-400 rounded-full"
                        initial={{ width: 0 }}
                        whileInView={{ width: '45%' }}
                        transition={{ duration: 1, delay: 0.7 }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section Plateforme - Nouveau design inspiré des UI modernes sur Dribbble */}
      <section 
        ref={platformRef}
        className="py-24 px-4 bg-white relative overflow-hidden" 
        id="plateforme"
      >
        {/* Éléments décoratifs subtils */}
        <div className="absolute right-0 top-0 w-1/3 h-1/3 bg-orange-50 opacity-60 rounded-bl-full -z-10"></div>
        <div className="absolute left-0 bottom-0 w-1/4 h-1/4 bg-gray-50 opacity-70 rounded-tr-full -z-10"></div>
        
        <div className="max-w-7xl mx-auto relative">
          <motion.div 
            className="text-center mb-20"
            initial="hidden"
            animate={platformControls}
            variants={staggerContainer}
          >
            <motion.div variants={scaleIn} className="inline-block bg-orange-100 text-[#E84E0E] text-sm font-semibold px-4 py-1 rounded-full mb-4">
              NOTRE PLATEFORME
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">
              Gestion simplifiée de vos campagnes publicitaires
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-3xl mx-auto">
              Notre plateforme intuitive vous permet de créer, diffuser et analyser vos campagnes d'affichage digital en quelques clics
            </motion.p>
          </motion.div>
          
          {/* Showcase de la plateforme avec design inspiré des UI dashboards sur Dribbble */}
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24"
            initial="hidden"
            animate={platformControls}
            variants={staggerContainer}
          >
            {/* Image de la plateforme */}
            <motion.div 
              className="relative order-2 lg:order-1"
              variants={slideInLeft}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
                {/* Interface de la plateforme */}
                <div className="bg-white p-4">
                  {/* Barre supérieure de navigation */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-6">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-[#E84E0E] rounded flex items-center justify-center mr-2">
                          <span className="text-white font-bold text-xs">A</span>
                        </div>
                        <span className="text-gray-900 font-medium">DOOH Platform</span>
                      </div>
                      <div className="hidden md:flex space-x-4 text-sm">
                        <a href="#" className="text-[#E84E0E] font-medium">Dashboard</a>
                        <a href="#" className="text-gray-600 hover:text-gray-900">Campagnes</a>
                        <a href="#" className="text-gray-600 hover:text-gray-900">Écrans</a>
                        <a href="#" className="text-gray-600 hover:text-gray-900">Analytics</a>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600">
                        <BellIcon className="h-4 w-4" />
                      </div>
                      <div className="h-8 w-8 bg-gray-800 rounded-full flex items-center justify-center text-white text-xs font-medium">
                        JD
                      </div>
                    </div>
                  </div>
                  
                  {/* Contenu principal */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-gray-900">Tableau de bord</h3>
                      <button className="bg-[#E84E0E] text-white text-sm py-2 px-4 rounded-lg flex items-center">
                        <PlusIcon className="h-4 w-4 mr-1" /> 
                        <span>Nouvelle campagne</span>
                      </button>
                    </div>
                    
                    {/* Statistiques */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {[
                        { label: "Campagnes actives", value: "12", icon: <ChartPieIcon className="h-4 w-4" />, color: "bg-blue-100 text-blue-600" },
                        { label: "Écrans réservés", value: "48", icon: <DeviceMobileIcon className="h-4 w-4" />, color: "bg-green-100 text-green-600" },
                        { label: "Budget mensuel", value: "3.2M", icon: <CreditCardIcon className="h-4 w-4" />, color: "bg-purple-100 text-purple-600" }
                      ].map((stat, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${stat.color}`}>
                              {stat.icon}
                            </div>
                            <span className="text-gray-600 text-sm">{stat.label}</span>
                          </div>
                          <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                        </div>
                      ))}
                    </div>
                    
                    {/* Liste des campagnes */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                      <div className="p-4 border-b border-gray-100">
                        <h4 className="font-medium text-gray-900">Campagnes récentes</h4>
                      </div>
                      <div>
                        {[
                          { name: "Lancement produit été", date: "01 Jun - 30 Jun", status: "active", progress: 45 },
                          { name: "Promotion centre commercial", date: "15 Mai - 15 Jun", status: "active", progress: 72 },
                          { name: "Événement culturel", date: "10 Jun - 20 Jun", status: "pending", progress: 0 }
                        ].map((campaign, idx) => (
                          <div key={idx} className="p-4 flex items-center justify-between border-b border-gray-100 last:border-none">
                            <div>
                              <h5 className="font-medium text-gray-900 mb-1">{campaign.name}</h5>
                              <div className="flex items-center text-sm text-gray-500">
                                <CalendarIcon className="h-3 w-3 mr-1" />
                                <span>{campaign.date}</span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4">
                              <div>
                                <div className="flex items-center mb-1">
                                  <div className="flex-1 h-2 w-24 bg-gray-100 rounded-full mr-2">
                                    <div 
                                      className={`h-full rounded-full ${
                                        campaign.status === "active" ? "bg-green-500" : "bg-yellow-400"
                                      }`}
                                      style={{ width: `${campaign.progress}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs text-gray-600">{campaign.progress}%</span>
                                </div>
                                <div className="flex items-center">
                                  <div className={`h-2 w-2 rounded-full mr-1 ${
                                    campaign.status === "active" ? "bg-green-500" : "bg-yellow-400"
                                  }`}></div>
                                  <span className="text-xs text-gray-600 capitalize">{campaign.status}</span>
                                </div>
                              </div>
                              <button className="text-gray-400 hover:text-gray-700">
                                <ChevronRightIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Éléments décoratifs */}
              <motion.div 
                className="absolute -top-8 -left-8 h-16 w-16 bg-[#E84E0E] rounded-full opacity-60 blur-xl"
                animate={{ 
                  scale: [1, 1.3, 1],
                }}
                transition={{ 
                  duration: 4,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              />
              <motion.div 
                className="absolute -bottom-10 -right-10 h-24 w-24 rounded-full border-4 border-orange-200"
                animate={{ 
                  rotate: 360,
                }}
                transition={{ 
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            </motion.div>
            
            {/* Texte des fonctionnalités */}
            <motion.div 
              className="order-1 lg:order-2"
              variants={slideInRight}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">Interface intuitive et puissante</h3>
              <p className="text-lg text-gray-600 mb-8">
                Notre plateforme a été conçue avec une approche centrée sur l'utilisateur, 
                vous permettant de gérer efficacement vos campagnes publicitaires digitales 
                sans expertise technique préalable.
              </p>
              
              <div className="space-y-8">
                {[
                  {
                    icon: <GlobeAltIcon className="h-6 w-6 text-white" />,
                    color: "bg-gradient-to-r from-[#E84E0E] to-orange-500",
                    title: "Cartographie interactive",
                    description: "Visualisez et sélectionnez vos écrans sur une carte interactive pour une planification stratégique optimale."
                  },
                  {
                    icon: <CalendarIcon className="h-6 w-6 text-white" />,
                    color: "bg-gradient-to-r from-blue-500 to-blue-600",
                    title: "Planification simplifiée",
                    description: "Créez et planifiez vos campagnes en quelques clics avec notre interface de calendrier intuitive."
                  },
                  {
                    icon: <PhotographIcon className="h-6 w-6 text-white" />,
                    color: "bg-gradient-to-r from-purple-500 to-purple-600",
                    title: "Gestion de contenu flexible",
                    description: "Importez, prévisualisez et organisez vos créations publicitaires, avec support pour tous les formats standards."
                  },
                  {
                    icon: <ChartBarIcon className="h-6 w-6 text-white" />,
                    color: "bg-gradient-to-r from-green-500 to-green-600",
                    title: "Analytics en temps réel",
                    description: "Suivez et analysez les performances de vos campagnes avec des tableaux de bord détaillés et personnalisables."
                  }
                ].map((feature, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-start"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.2 }}
                    whileHover={{ x: 5 }}
                  >
                    <div className={`mr-6 rounded-xl p-3 shadow-lg ${feature.color} mt-1`}>
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-2 text-gray-900">{feature.title}</h4>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <motion.button 
                className="mt-10 bg-gradient-to-r from-[#E84E0E] to-orange-600 hover:from-[#E84E0E] hover:to-[#E84E0E] text-white font-bold py-3 px-8 rounded-lg shadow-xl flex items-center"
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 15px 30px -5px rgba(232, 78, 14, 0.4)" 
                }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <span>Découvrir la plateforme</span>
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </motion.button>
            </motion.div>
          </motion.div>
          
          {/* Features en blocs avec design inspiré des UI cards de Dribbble */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10"
            initial="hidden"
            animate={platformControls}
            variants={staggerContainer}
          >
            {[
              {
                icon: <LightningBoltIcon className="h-10 w-10 text-[#E84E0E]" />,
                title: "Mise en ligne instantanée",
                description: "Vos campagnes sont diffusées sur nos écrans en quelques minutes seulement après validation."
              },
              {
                icon: <CursorClickIcon className="h-10 w-10 text-[#E84E0E]" />,
                title: "Achat simplifié",
                description: "Réservez, payez et gérez vos espaces publicitaires en quelques clics depuis votre espace personnel."
              },
              {
                icon: <CreditCardIcon className="h-10 w-10 text-[#E84E0E]" />,
                title: "Tarification transparente",
                description: "Profitez de notre tarification dynamique avec des options adaptées à tous les budgets."
              }
            ].map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 relative overflow-hidden group"
                variants={scaleIn}
                transition={{ delay: index * 0.2 }}
                whileHover={{ 
                  y: -10,
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)"
                }}
              >
                {/* Arrière-plan décoratif */}
                <div className="absolute -right-20 -top-20 h-40 w-40 bg-orange-50 rounded-full group-hover:scale-150 transition-transform duration-500"></div>
                
                <div className="relative">
                  <div className="mb-6">{feature.icon}</div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                  
                  {/* Animation de l'icône au survol */}
                  <motion.div 
                    className="absolute right-0 bottom-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    whileHover={{ scale: 1.1 }}
                  >
                    <ArrowRightIcon className="h-6 w-6 text-[#E84E0E]" />
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          
          {/* Bande de témoignage - Style inspiré des testimonials sur Dribbble */}
          <motion.div 
            className="mt-20 bg-gradient-to-r from-gray-900 to-black rounded-3xl overflow-hidden shadow-2xl"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="px-8 py-10 sm:p-12 relative overflow-hidden">
              {/* Motifs décoratifs */}
              <div className="absolute inset-0">
                <motion.div 
                  className="absolute -left-20 top-1/2 w-40 h-40 bg-[#E84E0E] rounded-full opacity-20 blur-3xl"
                  animate={{ 
                    y: [0, -30, 0],
                    opacity: [0.1, 0.3, 0.1]
                  }}
                  transition={{ 
                    duration: 8,
                    repeat: Infinity,
                    repeatType: "mirror"
                  }}
                />
                <motion.div 
                  className="absolute -right-20 bottom-0 w-60 h-60 bg-purple-600 rounded-full opacity-10 blur-3xl"
                  animate={{ 
                    y: [0, -40, 0],
                    opacity: [0.05, 0.2, 0.05]
                  }}
                  transition={{ 
                    duration: 10,
                    repeat: Infinity,
                    repeatType: "mirror",
                    delay: 1
                  }}
                />
              </div>
              
              {/* Contenu */}
              <div className="relative flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 mb-8 lg:mb-0 lg:pr-10">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  
                  <blockquote className="text-2xl sm:text-3xl font-medium text-white italic mb-6">
                    "La plateforme DOOH d'Accent Media a transformé notre stratégie publicitaire. 
                    L'interface intuitive et les rapports détaillés nous permettent d'optimiser nos campagnes en temps réel."
                  </blockquote>
                  
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-r from-[#E84E0E] to-orange-600 flex items-center justify-center text-white font-bold mr-4">
                      MS
                    </div>
                    <div>
                      <p className="text-white font-bold">Martine Sauvage</p>
                      <p className="text-gray-400">Directrice Marketing, SuperBrand</p>
                    </div>
                  </div>
                </div>
                
                <div className="lg:w-1/2 lg:pl-10">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-5 border border-white border-opacity-20">
                      <div className="flex items-center mb-3">
                        <EyeIcon className="h-5 w-5 text-[#E84E0E] mr-2" />
                        <h4 className="font-semibold text-white">Visibilité</h4>
                      </div>
                      <p className="text-gray-300 text-sm">+45% d'augmentation de notoriété après 3 mois de campagne</p>
                    </div>
                    <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-5 border border-white border-opacity-20">
                      <div className="flex items-center mb-3">
                        <ChartBarIcon className="h-5 w-5 text-[#E84E0E] mr-2" />
                        <h4 className="font-semibold text-white">Engagement</h4>
                      </div>
                      <p className="text-gray-300 text-sm">+68% de trafic en magasin pendant les périodes de diffusion</p>
                    </div>
                    <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-5 border border-white border-opacity-20">
                      <div className="flex items-center mb-3">
                        <CashIcon className="h-5 w-5 text-[#E84E0E] mr-2" />
                        <h4 className="font-semibold text-white">ROI</h4>
                      </div>
                      <p className="text-gray-300 text-sm">Retour sur investissement de 215% sur la première année</p>
                    </div>
                    <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-5 border border-white border-opacity-20">
                      <div className="flex items-center mb-3">
                        <LightningBoltIcon className="h-5 w-5 text-[#E84E0E] mr-2" />
                        <h4 className="font-semibold text-white">Rapidité</h4>
                      </div>
                      <p className="text-gray-300 text-sm">Mise en place des campagnes en moins de 24h contre 2 semaines auparavant</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section Réseau d'Écrans - Redesign inspiré des interfaces cartographiques modernes */}
      <section 
        ref={screensRef}
        className="py-24 px-4 bg-gray-50 relative overflow-hidden" 
        id="écrans"
      >
        {/* Fond décoratif abstrait */}
        <div className="absolute inset-0 opacity-30 overflow-hidden">
          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#E84E0E', stopOpacity: 0.1 }} />
                <stop offset="100%" style={{ stopColor: '#E84E0E', stopOpacity: 0 }} />
              </linearGradient>
            </defs>
            <path d="M0,0 L100,0 L100,100 C80,90 50,110 20,80 L0,100 Z" fill="url(#grad1)" />
          </svg>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            animate={screensControls}
            variants={staggerContainer}
          >
            <motion.div variants={scaleIn} className="inline-block bg-orange-100 text-[#E84E0E] text-sm font-semibold px-4 py-1 rounded-full mb-4">
              NOTRE RÉSEAU
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">
              Découvrez notre ceinture digitale
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-3xl mx-auto">
              Plus de 100 écrans stratégiquement placés dans les zones à fort trafic pour maximiser l'impact de vos campagnes
            </motion.p>
          </motion.div>
          
          {/* Carte interactive redesignée - Inspirée des UI cartographiques modernes */}
          <motion.div 
            className="mb-20"
            initial="hidden"
            animate={screensControls}
            variants={fadeInUp}
          >
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200">
              {/* Barre d'outils de la carte */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex flex-col md:flex-row justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900 flex items-center mb-4 md:mb-0">
                  <LocationMarkerIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
                  Carte des emplacements
                </h3>
                
                <div className="flex flex-wrap gap-2">
                  <div className="relative">
                    <select className="bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-10 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#E84E0E]">
                      <option>Tous les types</option>
                      <option>Grand format</option>
                      <option>Écrans LCD</option>
                      <option>Totems interactifs</option>
                    </select>
                    <ChevronDownIcon className="h-4 w-4 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                  </div>
                  
                  <div className="relative">
                    <select className="bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-10 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#E84E0E]">
                      <option>Toutes les zones</option>
                      <option>Centre-ville</option>
                      <option>Centres commerciaux</option>
                      <option>Quartier des affaires</option>
                    </select>
                    <ChevronDownIcon className="h-4 w-4 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                  </div>
                  
                  <div className="relative">
                    <select className="bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-10 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#E84E0E]">
                      <option>Tous les formats</option>
                      <option>Paysage</option>
                      <option>Portrait</option>
                      <option>Carré</option>
                    </select>
                    <ChevronDownIcon className="h-4 w-4 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                  </div>
                  
                  <button className="bg-[#E84E0E] text-white text-sm py-2 px-4 rounded-lg flex items-center">
                    <FilterIcon className="h-4 w-4 mr-1" />
                    <span>Filtrer</span>
                  </button>
                </div>
              </div>
              
              {/* Carte principale */}
              <div className="relative h-[500px] md:h-[600px] bg-gray-100 overflow-hidden">
                {/* Image de carte en fond */}
                <img 
                  src="https://images.unsplash.com/photo-1526778548025-fa2f459cd5ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
                  alt="Carte de la ville" 
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
                />
                
                {/* Overlay sur la carte */}
                <div className="absolute inset-0 bg-gray-900 opacity-40"></div>
                
                {/* Emplacements des écrans - Design inspiré des UI de mapping sur Dribbble */}
                {[...Array(12)].map((_, index) => {
                  const positions = [
                    { left: '20%', top: '30%', name: 'Centre Commercial Élite', type: 'Grand écran LED', visitors: '15K/jour' },
                    { left: '50%', top: '20%', name: 'Place du Marché Central', type: 'Écran LED extérieur', visitors: '12K/jour' },
                    { left: '80%', top: '40%', name: 'Gare Centrale', type: 'Totem interactif', visitors: '22K/jour' },
                    { left: '35%', top: '60%', name: 'Avenue Principale', type: 'Mur digital', visitors: '18K/jour' },
                    { left: '70%', top: '70%', name: 'Galerie Marchande', type: 'Écran LCD HD', visitors: '9K/jour' },
                    { left: '15%', top: '75%', name: 'Boulevard Sud', type: 'Écran LED extérieur', visitors: '7K/jour' },
                    { left: '60%', top: '50%', name: 'Zone Piétonne', type: 'Totem interactif', visitors: '20K/jour' },
                    { left: '90%', top: '80%', name: 'Terminal Aéroport', type: 'Écran LED', visitors: '25K/jour' },
                    { left: '40%', top: '30%', name: 'Quartier des Affaires', type: 'Écran géant', visitors: '17K/jour' },
                    { left: '75%', top: '25%', name: 'Esplanade Culturelle', type: 'Mur LED', visitors: '8K/jour' },
                    { left: '25%', top: '15%', name: 'Centre Sportif', type: 'Écran extérieur', visitors: '14K/jour' },
                    { left: '65%', top: '85%', name: 'Parc Commercial', type: 'Grand format LED', visitors: '11K/jour' },
                  ];
                  
                  return (
                    <motion.div
                      key={index}
                      className="absolute cursor-pointer group z-20"
                      style={positions[index]}
                      initial={{ scale: 0 }}
                      whileInView={{ 
                        scale: [0, 1.2, 1],
                      }}
                      transition={{ 
                        delay: index * 0.1,
                        duration: 0.8
                      }}
                    >
                      <div className="relative">
                        {/* Cercle pulsant autour du marqueur */}
                        <div className="absolute inset-0 -m-1 rounded-full bg-[#E84E0E] opacity-30 group-hover:opacity-40 animate-ping" style={{ animationDuration: '3s' }}></div>
                        
                        {/* Marqueur principal */}
                        <div className="w-8 h-8 bg-gradient-to-r from-[#E84E0E] to-orange-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200 border-2 border-white">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                        
                        {/* Info-bulle de localisation */}
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-3 bg-white rounded-xl p-3 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-48 border border-gray-200 z-30">
                          <div className="font-bold text-gray-900 mb-1">{positions[index].name}</div>
                          <div className="text-sm text-gray-500 mb-2">{positions[index].type}</div>
                          <div className="flex items-center text-xs text-gray-700">
                            <EyeIcon className="h-3 w-3 mr-1 text-[#E84E0E]" />
                            <span>{positions[index].visitors}</span>
                          </div>
                          
                          {/* Flèche pointant vers le marqueur */}
                          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 transform rotate-45 w-3 h-3 bg-white border-r border-b border-gray-200"></div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
                
                {/* Contrôles de la carte en overlay */}
                <div className="absolute bottom-6 left-6 right-6 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                  <div className="bg-white rounded-xl shadow-xl p-4 w-full md:w-auto">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="h-3 w-3 rounded-full bg-[#E84E0E]"></div>
                      <span className="text-sm font-medium text-gray-900">100+ écrans disponibles</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button className="bg-[#E84E0E] text-white text-xs py-1 px-3 rounded-full">Tous</button>
                      <button className="bg-gray-100 text-gray-700 text-xs py-1 px-3 rounded-full hover:bg-gray-200">Centre-ville</button>
                      <button className="bg-gray-100 text-gray-700 text-xs py-1 px-3 rounded-full hover:bg-gray-200">Commerces</button>
                      <button className="bg-gray-100 text-gray-700 text-xs py-1 px-3 rounded-full hover:bg-gray-200">Transports</button>
                    </div>
                  </div>
                  
                  <motion.button 
                    className="bg-gradient-to-r from-[#E84E0E] to-orange-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg flex items-center space-x-2 w-full md:w-auto justify-center"
                    whileHover={{ scale: 1.05, boxShadow: "0 15px 30px -5px rgba(232, 78, 14, 0.4)" }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>Explorer tous les emplacements</span>
                    <ArrowRightIcon className="h-5 w-5" />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Catégories d'écrans - Design inspiré des cards modernes de Dribbble */}
          <motion.div 
            className="mb-20"
            initial="hidden"
            animate={screensControls}
            variants={staggerContainer}
          >
            <motion.h3 
              variants={fadeInUp}
              className="text-2xl md:text-3xl font-bold mb-10 text-center text-gray-900"
            >
              Types d'écrans disponibles
            </motion.h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
                  title: "Écrans LED extérieurs",
                  description: "Idéals pour une visibilité maximale dans les zones urbaines à fort trafic",
                  specs: "Format jusqu'à 8x5m, 4K UHD",
                  features: ["Haute luminosité", "Résistant aux intempéries", "Visibilité jusqu'à 300m"]
                },
                {
                  image: "https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
                  title: "Écrans LCD indoor",
                  description: "Parfaits pour les centres commerciaux et espaces intérieurs",
                  specs: "Format de 55\" à 85\", Full HD",
                  features: ["Haute définition", "Son intégré disponible", "Interactivité optionnelle"]
                },
                {
                  image: "https://images.unsplash.com/photo-1519951468518-33c8bee8ff61?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
                  title: "Totems interactifs",
                  description: "Engagez votre audience avec des expériences tactiles immersives",
                  specs: "Format 2x3m, écran tactile",
                  features: ["Multi-touch", "Connexion Internet", "Analytics avancés"]
                }
              ].map((screen, index) => (
                <motion.div 
                  key={index}
                  className="bg-white rounded-2xl overflow-hidden shadow-xl group relative"
                  variants={scaleIn}
                  transition={{ delay: index * 0.2 }}
                  whileHover={{ 
                    y: -8,
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                  }}
                >
                  <div className="h-52 md:h-64 overflow-hidden relative">
                    <img 
                      src={screen.image}
                      alt={screen.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h4 className="font-bold text-lg">{screen.title}</h4>
                      <p className="text-white text-opacity-80 text-sm">{screen.specs}</p>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <p className="text-gray-600 mb-4">{screen.description}</p>
                    <div className="space-y-2 mb-6">
                      {screen.features.map((feature, i) => (
                        <div key={i} className="flex items-center">
                          <CheckCircleIcon className="h-5 w-5 mr-2 text-green-500" />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <button className="text-[#E84E0E] font-medium flex items-center text-sm hover:underline">
                      En savoir plus
                      <ArrowRightIcon className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          {/* Avantages en deux colonnes - Design inspiré des dashboards sur Dribbble */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial="hidden"
            animate={screensControls}
            variants={staggerContainer}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 relative overflow-hidden"
              variants={slideInLeft}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="absolute -right-20 -top-20 w-40 h-40 bg-orange-50 rounded-full transform -rotate-12"></div>
              
              <div className="relative">
                <div className="flex items-start mb-6">
                  <div className="mr-4 p-3 rounded-xl shadow-lg bg-gradient-to-br from-[#E84E0E] to-orange-600">
                    <CashIcon className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">Tarification dynamique</h3>
                    <p className="text-gray-600">Des prix adaptés à tous les budgets grâce à notre système intelligent qui s'ajuste selon la demande et la saisonnalité</p>
                  </div>
                </div>
                
                <div className="space-y-4 mb-8">
                  {["Paiement à l'usage", "Pas d'engagement minimum", "Optimisation automatique du budget"].map((item, i) => (
                    <div key={i} className="flex items-center">
                      <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3">
                        <CheckCircleIcon className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-gray-700 font-medium">Tarif moyen par écran</span>
                    <div className="flex space-x-1">
                      <span className="text-xs py-1 px-2 bg-green-100 text-green-800 rounded-full">-15% vs marché</span>
                    </div>
                  </div>
                  <div className="flex items-end space-x-2 mb-4">
                    <span className="text-3xl font-bold text-gray-900">2500</span>
                    <span className="text-gray-500 pb-1">FCFA/heure</span>
                  </div>
                  <button className="w-full py-3 px-4 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 font-medium rounded-xl hover:from-gray-200 hover:to-gray-300 transition-colors flex items-center justify-center">
                    <span>Voir la grille tarifaire</span>
                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 relative overflow-hidden"
              variants={slideInRight}
              transition={{ delay: 0.3 }}
              whileHover={{ y: -5 }}
            >
              <div className="absolute -left-20 -bottom-20 w-40 h-40 bg-blue-50 rounded-full transform -rotate-12"></div>
              
              <div className="relative">
                <div className="flex items-start mb-6">
                  <div className="mr-4 p-3 rounded-xl shadow-lg bg-gradient-to-br from-blue-500 to-blue-600">
                    <ChartBarIcon className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">Analytics avancés</h3>
                    <p className="text-gray-600">Mesurez l'impact de vos campagnes avec des données précises sur l'audience touchée et l'engagement généré</p>
                  </div>
                </div>
                
                <div className="space-y-4 mb-6">
                  {["Rapports en temps réel", "Estimation d'audience", "Segmentation démographique", "Export de données"].map((item, i) => (
                    <div key={i} className="flex items-center">
                      <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                        <CheckCircleIcon className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="h-32 relative">
                    <div className="absolute inset-0 flex items-end justify-around">
                      {[60, 75, 45, 85, 65, 90, 70].map((height, i) => (
                        <div key={i} className="h-full flex-1 flex flex-col justify-end px-0.5">
                          <motion.div
                            className="bg-gradient-to-t from-blue-500 to-[#E84E0E] rounded-t-sm"
                            initial={{ height: 0 }}
                            whileInView={{ height: `${height}%` }}
                            transition={{ duration: 1, delay: i * 0.1 }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      {/* Section Partenariat - Design moderne inspiré des landing pages premium */}
      <section 
        ref={partnersRef}
        className="py-24 px-4 bg-white relative overflow-hidden" 
        id="partenariat"
      >
        {/* Éléments décoratifs de fond */}
        <div className="absolute right-0 top-0 h-64 w-64 bg-orange-50 rounded-bl-full opacity-70 -z-10"></div>
        <div className="absolute left-0 bottom-0 h-80 w-80 bg-gray-50 rounded-tr-full opacity-80 -z-10"></div>
        
        <div className="max-w-7xl mx-auto relative">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            animate={partnersControls}
            variants={staggerContainer}
          >
            <motion.div variants={scaleIn} className="inline-block bg-orange-100 text-[#E84E0E] text-sm font-semibold px-4 py-1 rounded-full mb-4">
              DEVENEZ PARTENAIRE
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">
              Monétisez vos espaces inutilisés
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-3xl mx-auto">
              Vous possédez un emplacement stratégique ? Rejoignez notre réseau et transformez vos espaces en source de revenus passifs
            </motion.p>
          </motion.div>
          
          {/* Carte principale du partenariat - Design inspiré des présentations business premium */}
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-24"
            initial="hidden"
            animate={partnersControls}
            variants={staggerContainer}
          >
            {/* Image avec overlay informatif */}
            <motion.div 
              className="lg:col-span-5 rounded-2xl overflow-hidden shadow-2xl relative h-full min-h-[400px] lg:min-h-[600px]"
              variants={slideInLeft}
            >
              <img 
                src="https://images.unsplash.com/photo-1577412647305-991150c7d163?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
                alt="Espace commercial moderne avec écran digital" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent"></div>
              
              <div className="absolute inset-x-0 bottom-0 p-8 text-white">
                <h3 className="text-3xl font-bold mb-4">
                  <span className="block">Générez des</span>
                  <span className="block">revenus <span className="text-[#E84E0E]">passifs</span></span>
                </h3>
                <p className="text-lg opacity-90 mb-6">Transformez vos murs et espaces inutilisés en source de revenus constants et prévisibles</p>
                
                {/* Indicateurs de performance */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {[
                    { value: "20%", label: "Taux de commission moyen" },
                    { value: "+10%", label: "Bonus trimestriel de performance" },
                    { value: "24/7", label: "Support dédié partenaires" }
                  ].map((stat, i) => (
                    <div key={i} className="text-center bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-3 border border-white border-opacity-20">
                      <p className="text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs mt-1 text-white text-opacity-80">{stat.label}</p>
                    </div>
                  ))}
                </div>
                
                <button className="bg-white text-[#E84E0E] py-3 px-6 rounded-lg font-bold hover:bg-opacity-90 transition-colors flex items-center shadow-lg">
                  <span>Découvrir les avantages</span>
                  <ChevronRightIcon className="h-5 w-5 ml-1" />
                </button>
              </div>
            </motion.div>
            
            {/* Contenu informatif */}
            <motion.div 
              className="lg:col-span-7 flex flex-col justify-center"
              variants={slideInRight}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">Un partenariat gagnant-gagnant</h3>
              <p className="text-lg text-gray-600 mb-10">
                Notre modèle de partenariat est conçu pour créer une relation mutuellement bénéfique. 
                Nous prenons en charge tous les aspects techniques et vous proposons une transparence totale 
                sur les revenus générés par les écrans installés dans vos espaces.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                {[
                  {
                    icon: <SparklesIcon className="h-6 w-6 text-white" />,
                    color: "bg-gradient-to-r from-[#E84E0E] to-orange-600",
                    title: "Revenus passifs",
                    description: "Pourcentage fixe sur chaque diffusion sur votre emplacement, entre 15% et 25% selon la localisation"
                  },
                  {
                    icon: <CurrencyDollarIcon className="h-6 w-6 text-white" />,
                    color: "bg-gradient-to-r from-green-500 to-green-600",
                    title: "Bonus de performance",
                    description: "Bonus trimestriel basé sur le taux d'occupation des écrans, jusqu'à 10% supplémentaires"
                  },
                  {
                    icon: <UserGroupIcon className="h-6 w-6 text-white" />,
                    color: "bg-gradient-to-r from-blue-500 to-blue-600",
                    title: "Bonus de recommandation",
                    description: "Commission majorée lorsque vous apportez directement des annonceurs à la plateforme (+5% à +10%)"
                  },
                  {
                    icon: <ShieldCheckIcon className="h-6 w-6 text-white" />,
                    color: "bg-gradient-to-r from-purple-500 to-purple-600",
                    title: "Contrôle du contenu",
                    description: "Validation préalable des types de campagnes pouvant être diffusées sur vos écrans"
                  }
                ].map((benefit, index) => (
                  <motion.div 
                    key={index} 
                    className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.2 }}
                    whileHover={{ y: -5, boxShadow: "0 20px 30px -10px rgba(0, 0, 0, 0.1)" }}
                  >
                    <div className={`h-12 w-12 ${benefit.color} rounded-xl shadow-lg flex items-center justify-center mb-5`}>
                      {benefit.icon}
                    </div>
                    <h4 className="font-bold text-lg mb-3 text-gray-900">{benefit.title}</h4>
                    <p className="text-gray-600 text-sm">{benefit.description}</p>
                  </motion.div>
                ))}
              </div>
              
              {/* Simulateur de revenus */}
              <motion.div 
                className="bg-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <h4 className="font-bold text-lg mb-4 text-gray-900">Simulateur de revenus partenaire</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2 font-medium">Surface disponible (m²)</label>
                    <div className="relative">
                      <input 
                        type="number" 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent" 
                        defaultValue="10"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500">
                        m²
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-2 font-medium">Passage quotidien estimé</label>
                    <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent appearance-none bg-white">
                      <option>Faible (100-500)</option>
                      <option selected>Moyen (500-2000)</option>
                      <option>Élevé (2000+)</option>
                    </select>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-[#E84E0E] to-orange-600 text-white p-6 rounded-xl shadow-lg">
                  <div className="flex justify-between items-center mb-2">
                    <p className="font-medium">Estimation mensuelle</p>
                    <span className="text-xs py-1 px-3 bg-white bg-opacity-20 rounded-full">
                      Commission de 20%
                    </span>
                  </div>
                  <p className="text-3xl font-bold">150 000 - 300 000 FCFA</p>
                  <div className="mt-3 text-sm text-white text-opacity-80">
                    Ces estimations peuvent varier selon l'emplacement exact et la saisonnalité
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
          
          {/* Processus en 4 étapes - Design inspiré des roadmaps sur Dribbble */}
          <motion.div
            className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gray-50 px-8 py-6 border-b border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900">Comment ça marche ?</h3>
              <p className="text-gray-600">Notre processus de partenariat en 4 étapes simples</p>
            </div>
            
            <div className="p-8">
              <div className="relative">
                {/* Ligne de connexion - pour desktop */}
                <div className="hidden md:block absolute top-16 left-[60px] right-[60px] h-1 bg-gradient-to-r from-[#E84E0E] via-blue-500 to-green-500 rounded-full z-0"></div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative z-10">
                  {[
                    {
                      step: "01",
                      title: "Contact",
                      description: "Prenez contact avec notre équipe partenariat pour discuter de votre emplacement",
                      icon: <UserGroupIcon className="h-8 w-8 text-white" />,
                      color: "from-[#E84E0E] to-orange-600"
                    },
                    {
                      step: "02",
                      title: "Évaluation",
                      description: "Nos experts évaluent le potentiel de votre emplacement et la faisabilité technique",
                      icon: <ChartBarIcon className="h-8 w-8 text-white" />,
                      color: "from-blue-500 to-blue-600"
                    },
                    {
                      step: "03",
                      title: "Déploiement",
                      description: "Installation professionnelle de l'écran et configuration de votre espace partenaire",
                      icon: <CubeTransparentIcon className="h-8 w-8 text-white" />,
                      color: "from-purple-500 to-purple-600"
                    },
                    {
                      step: "04",
                      title: "Monétisation",
                      description: "Commencez à percevoir des revenus dès la mise en service de l'écran",
                      icon: <CurrencyDollarIcon className="h-8 w-8 text-white" />,
                      color: "from-green-500 to-green-600"
                    }
                  ].map((step, index) => (
                    <motion.div 
                      key={index} 
                      className="flex flex-col items-center text-center relative"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 + index * 0.15 }}
                    >
                      <div className="mb-6 w-16 h-16 bg-gradient-to-r rounded-full flex items-center justify-center shadow-lg relative z-10"
                           style={{ backgroundImage: `linear-gradient(to right, ${step.color})` }}>
                        {step.icon}
                      </div>
                      <div className="text-sm font-bold text-[#E84E0E] mb-2">ÉTAPE {step.step}</div>
                      <h4 className="text-xl font-bold mb-3 text-gray-900">{step.title}</h4>
                      <p className="text-gray-600">{step.description}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-center">
              <motion.button 
                className="flex items-center text-[#E84E0E] font-semibold group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Télécharger le guide complet du partenariat</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 group-hover:translate-y-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section Témoignages - Design inspiré des cards modernes */}
      <section 
        ref={testimonialsRef}
        className="py-24 px-4 bg-gray-50 relative overflow-hidden" 
      >
        {/* Éléments décoratifs */}
        <div className="absolute -left-20 top-0 h-40 w-40 bg-[#E84E0E] opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute right-0 bottom-0 h-40 w-40 bg-blue-500 opacity-5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            animate={testimonialsControls}
            variants={staggerContainer}
          >
            <motion.div variants={scaleIn} className="inline-block bg-orange-100 text-[#E84E0E] text-sm font-semibold px-4 py-1 rounded-full mb-4">
              TÉMOIGNAGES
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">
              Ce que disent nos clients
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-3xl mx-auto">
              Découvrez l'expérience des annonceurs et partenaires qui nous font confiance au quotidien
            </motion.p>
          </motion.div>
          
          {/* Grille de témoignages */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            initial="hidden"
            animate={testimonialsControls}
            variants={staggerContainer}
          >
            {[
              {
                quote: "La plateforme DOOH d'Accent Media a transformé notre approche publicitaire. L'interface intuitive nous permet de gérer nos campagnes en toute autonomie.",
                author: "Marie Dupont",
                position: "Directrice Marketing",
                company: "Retail Premium",
                avatar: "MD"
              },
              {
                quote: "Nous avons constaté une augmentation de 40% de notre trafic en magasin durant les périodes de diffusion sur le réseau d'Accent Media.",
                author: "Thomas Leroy",
                position: "CEO",
                company: "TechStore",
                avatar: "TL"
              },
              {
                quote: "En tant que partenaire, j'apprécie particulièrement la transparence du système et la régularité des revenus générés par les écrans installés dans nos locaux.",
                author: "Sophie Martin",
                position: "Gérante",
                company: "Centre Commercial Elite",
                avatar: "SM"
              }
            ].map((testimonial, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 relative"
                variants={scaleIn}
                transition={{ delay: index * 0.2 }}
                whileHover={{ y: -10 }}
              >
                {/* Guillemets décoratifs */}
                <div className="absolute -top-5 -left-2 text-8xl text-gray-100 opacity-50">"</div>
                
                <div className="relative">
                  <div className="mb-6">
                    <div className="flex mb-4">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-gray-700 italic">{testimonial.quote}</p>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-r from-[#E84E0E] to-orange-600 flex items-center justify-center text-white font-bold mr-4">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{testimonial.author}</p>
                      <p className="text-sm text-gray-600">{testimonial.position}, {testimonial.company}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          
          {/* Bannière de stats inspirée des dashboards */}
          <motion.div 
            className="mt-20 bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { value: "200+", label: "Clients actifs", icon: <UserIcon className="h-6 w-6 text-[#E84E0E]" /> },
                { value: "95%", label: "Taux de satisfaction", icon: <BadgeCheckIcon className="h-6 w-6 text-[#E84E0E]" /> },
                { value: "30k+", label: "Campagnes diffusées", icon: <LightningBoltIcon className="h-6 w-6 text-[#E84E0E]" /> },
                { value: "45+", label: "Partenaires d'affichage", icon: <UserGroupIcon className="h-6 w-6 text-[#E84E0E]" /> }
              ].map((stat, index) => (
                <motion.div 
                  key={index} 
                  className="flex items-center space-x-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                >
                  <div className="p-3 bg-orange-100 rounded-xl">
                    {stat.icon}
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                    <p className="text-gray-600">{stat.label}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section CTA principale - Design inspiré des CTA premium sur Dribbble */}
      <section className="py-20 px-4 relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            className="bg-gradient-to-br from-gray-900 to-black rounded-3xl overflow-hidden shadow-2xl"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Éléments graphiques abstraits */}
            <div className="absolute inset-0 overflow-hidden">
              {/* Formes géométriques */}
              <motion.div 
                className="absolute top-0 left-0 w-full h-full opacity-10"
                animate={{ rotate: 360 }}
                transition={{ duration: 180, repeat: Infinity, ease: "linear" }}
              >
                <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                  <path 
                    fill="#E84E0E" 
                    d="M48.8,-58.2C63.9,-49,77.3,-33.9,81.6,-16.5C85.9,0.9,81.2,20.5,70.6,35C60.1,49.5,43.8,58.9,26.9,62.8C10,66.7,-7.5,65.1,-25.5,60.3C-43.6,55.6,-62.2,47.6,-70.9,33.3C-79.5,19.1,-78.2,-1.3,-71.2,-18.9C-64.2,-36.4,-51.5,-51.2,-37,-59.8C-22.5,-68.5,-6.2,-71,9.5,-82.2C25.2,-93.4,50.5,-113.4,51.2,-104.5C51.8,-95.6,27.9,-57.8,21.7,-50.2C15.6,-42.5,33.6,-67.4,48.8,-58.2Z" 
                    transform="translate(100 100)" 
                  />
                </svg>
              </motion.div>
              
              {/* Cercle lumineux */}
              <motion.div 
                className="absolute -bottom-20 -right-20 h-80 w-80 bg-[#E84E0E] rounded-full opacity-10 blur-3xl"
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.1, 0.15, 0.1]
                }}
                transition={{ 
                  duration: 8,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              />
              
              {/* Lignes de grille */}
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zMCAzMGgxdjFoLTF6bTEgMGgxdjFoLTF6bS0yIDBoMXYxaC0xem0tMjkgMGgxdjFoLTF6bTMwLTFoMXYxaC0xeiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIgZmlsbC1ydWxlPSJub256ZXJvIi8+PC9nPjwvc3ZnPg==')] bg-[length:30px_30px] opacity-10"></div>
            </div>
            
            {/* Contenu principal */}
            <div className="relative z-10 p-12 md:p-16 lg:p-20">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7">
                  <motion.div 
                    className="inline-block bg-white bg-opacity-10 px-4 py-1 rounded-full mb-6 backdrop-blur-sm"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <span className="text-white text-sm font-semibold">LANCEZ VOTRE CAMPAGNE</span>
                  </motion.div>
                  
                  <motion.h2 
                    className="text-3xl md:text-5xl font-bold mb-6 text-white leading-tight"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                  >
                    Prêt à captiver votre audience avec un impact visuel maximal ?
                  </motion.h2>
                  
                  <motion.p 
                    className="text-lg md:text-xl text-white text-opacity-90 mb-10 max-w-2xl"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    Rejoignez plus de 200 annonceurs qui ont déjà adopté notre plateforme et faites briller votre marque sur nos écrans premium
                  </motion.p>
                  
                  <motion.div 
                    className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  >
                    <motion.button 
                      className="bg-white text-gray-900 font-bold py-4 px-8 rounded-xl shadow-xl hover:shadow-2xl transition duration-300 ease-in-out flex items-center justify-center"
                      whileHover={{ scale: 1.05, boxShadow: "0 25px 50px -12px rgba(255, 255, 255, 0.35)" }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>Créer un compte</span>
                      <ChevronRightIcon className="h-5 w-5 ml-2" />
                    </motion.button>
                    
                    <motion.button 
                      className="bg-transparent text-white border-2 border-white hover:bg-white hover:bg-opacity-10 font-bold py-4 px-8 rounded-xl shadow-lg transition duration-300 ease-in-out flex items-center justify-center"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>Demander une démo</span>
                    </motion.button>
                  </motion.div>
                </div>
                
                <div className="lg:col-span-5">
                  <motion.div 
                    className="bg-white bg-opacity-5 backdrop-blur-sm rounded-2xl p-8 border border-white border-opacity-10 shadow-2xl"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    
                    animate={{ 
                      y: [0, -10, 0],
                    }}
                    transition={{ 
                      y: {
                        duration: 6,
                        repeat: Infinity,
                        repeatType: "reverse"
                      }
                    }}
                  >
                    <div className="flex items-center mb-6">
                      <div className="h-12 w-12 bg-[#E84E0E] rounded-xl flex items-center justify-center mr-4 shadow-lg">
                        <BadgeCheckIcon className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-xl text-white">Résultats prouvés</h3>
                        <p className="text-white text-opacity-80 text-sm">Impact mesurable et garanti</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4 mb-8">
                      {[
                        { label: "Mémorisation de marque", value: 78 },
                        { label: "Engagement visuel", value: 92 },
                        { label: "Conversion en magasin", value: 45 }
                      ].map((stat, i) => (
                        <div key={i} className="bg-white bg-opacity-5 p-4 rounded-lg backdrop-blur-sm">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-white text-opacity-90">{stat.label}</span>
                            <span className="text-white font-bold">{stat.value}%</span>
                          </div>
                          <div className="h-2 bg-white bg-opacity-10 rounded-full overflow-hidden">
                            <motion.div 
                              className="h-full bg-gradient-to-r from-[#E84E0E] to-orange-500 rounded-full"
                              initial={{ width: 0 }}
                              whileInView={{ width: `${stat.value}%` }}
                              transition={{ duration: 1, delay: 0.5 + i * 0.2 }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-between text-center">
                      <div>
                        <p className="text-3xl font-bold text-white">20+</p>
                        <p className="text-sm text-white text-opacity-70">Villes couvertes</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-white">5M+</p>
                        <p className="text-sm text-white text-opacity-70">Impressions/mois</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-white">15+</p>
                        <p className="text-sm text-white text-opacity-70">Formats pris en charge</p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Section Contact - Redesign moderne inspiré des formulaires de contact premium */}
      <section className="py-24 px-4 bg-gray-50" id="contact">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block bg-orange-100 text-[#E84E0E] text-sm font-semibold px-4 py-1 rounded-full mb-4">
              CONTACTEZ-NOUS
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">
              Des questions ? Parlons-en
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Notre équipe est disponible pour répondre à toutes vos questions et vous accompagner dans votre projet
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Formulaire de contact */}
            <motion.div 
              className="lg:col-span-7 bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="p-8 md:p-10">
                <h3 className="text-2xl font-bold mb-8 text-gray-900 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-[#E84E0E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  Envoyez-nous un message
                </h3>
                
                <form>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-2">
                      <label className="block text-gray-700 font-medium">Nom</label>
                      <input 
                        type="text" 
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent transition-all"
                        placeholder="Votre nom"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-gray-700 font-medium">Email</label>
                      <input 
                        type="email" 
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent transition-all"
                        placeholder="Votre email"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-6 space-y-2">
                    <label className="block text-gray-700 font-medium">Sujet</label>
                    <div className="relative">
                      <select className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent transition-all appearance-none bg-white">
                        <option>Demande d'information</option>
                        <option>Devenir annonceur</option>
                        <option>Devenir partenaire</option>
                        <option>Support technique</option>
                        <option>Autre sujet</option>
                      </select>
                      <ChevronDownIcon className="h-5 w-5 text-gray-400 absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                  
                  <div className="mb-8 space-y-2">
                    <label className="block text-gray-700 font-medium">Message</label>
                    <textarea 
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent transition-all h-32 resize-none"
                      placeholder="Votre message"
                    ></textarea>
                  </div>
                  
                  <motion.button 
                    type="submit"
                    className="bg-gradient-to-r from-[#E84E0E] to-orange-600 hover:from-[#E84E0E] hover:to-[#E84E0E] text-white font-bold py-3 px-8 rounded-xl shadow-lg transition duration-300 ease-in-out w-full md:w-auto flex items-center justify-center"
                    whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(232, 78, 14, 0.4)" }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>Envoyer le message</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </motion.button>
                </form>
              </div>
            </motion.div>
            
            {/* Informations de contact */}
            <motion.div 
              className="lg:col-span-5 flex flex-col justify-between"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div>
                <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-[#E84E0E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Informations de contact
                </h3>
                <p className="text-lg text-gray-600 mb-8">
                  N'hésitez pas à nous contacter directement ou à passer dans nos bureaux pour en savoir plus sur notre plateforme DOOH.
                </p>
                
                <div className="space-y-6 mb-10">
                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="space-y-6">
                      {[
                        {
                          icon: <LocationMarkerIcon className="h-6 w-6 text-[#E84E0E]" />,
                          title: "Adresse",
                          description: "Douala, Cameroun",
                          action: "Voir sur la carte",
                          actionIcon: <ArrowRightIcon className="h-4 w-4 ml-1" />
                        },
                        {
                          icon: <svg className="h-6 w-6 text-[#E84E0E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>,
                          title: "Téléphone",
                          description: "+237 233 42 XX XX",
                          action: "Appeler maintenant",
                          actionIcon: <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                        },
                        {
                          icon: <svg className="h-6 w-6 text-[#E84E0E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
                          title: "Email",
                          description: "info@accentmedia.com",
                          action: "Envoyer un email",
                          actionIcon: <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                        }
                      ].map((info, index) => (
                        <motion.div 
                          key={index} 
                          className="flex items-start"
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: 0.3 + index * 0.2 }}
                        >
                          <div className="mr-4 p-3 bg-orange-100 rounded-xl">{info.icon}</div>
                          <div className="flex-1">
                            <h4 className="font-bold text-lg text-gray-900">{info.title}</h4>
                            <p className="text-gray-600 mb-2">{info.description}</p>
                            <a href="#" className="text-[#E84E0E] text-sm font-medium flex items-center hover:underline">
                              {info.action}
                              {info.actionIcon}
                            </a>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-4 text-gray-900 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E84E0E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                  </svg>
                  Suivez-nous
                </h3>
                
                <div className="flex space-x-4">
                  {['facebook', 'twitter', 'instagram', 'linkedin'].map((social, index) => (
                    <motion.a 
                      key={social} 
                      href={`#${social}`} 
                      className="h-12 w-12 rounded-xl bg-white flex items-center justify-center text-gray-600 hover:bg-[#E84E0E] hover:text-white transition-colors duration-300 shadow-md"
                      whileHover={{ y: -5, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                    >
                      <span className="sr-only">{social}</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        {social === 'facebook' && <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />}
                        {social === 'twitter' && <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />}
                        {social === 'instagram' && <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />}
                        {social === 'linkedin' && <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />}
                      </svg>
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer - Design moderne inspiré des footers premium sur Dribbble */}
      <footer className="bg-gray-900 text-white">
        {/* Footer Principal */}
        <div className="pt-20 pb-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-10">
              {/* Section Marque */}
              <div className="md:col-span-2">
                <div className="flex items-center mb-6">
                  <div className="h-12 w-12 bg-[#E84E0E] rounded-xl flex items-center justify-center mr-3 shadow-lg">
                    <span className="text-white font-bold text-2xl">A</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">ACCENT MEDIA</h3>
                    <p className="text-gray-400 text-sm">Digital Out Of Home</p>
                  </div>
                </div>
                <p className="text-gray-400 mb-8 pr-8">
                  L'innovation au service de la communication. Notre plateforme révolutionne la façon dont vous réservez et gérez vos campagnes d'affichage digital sur notre réseau d'écrans stratégiquement placés.
                </p>
                <div className="flex space-x-4">
                  {['facebook', 'twitter', 'instagram', 'linkedin'].map((social) => (
                    <a 
                      key={social} 
                      href={`#${social}`} 
                      className="h-10 w-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-[#E84E0E] hover:text-white transition-colors duration-300"
                      aria-label={social}
                    >
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        {social === 'facebook' && <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />}
                        {social === 'twitter' && <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />}
                        {social === 'instagram' && <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />}
                        {social === 'linkedin' && <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />}
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
              
              {/* Sections de liens */}
              <div className="md:col-span-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
                <div>
                  <h4 className="font-bold text-lg mb-5">Plateforme</h4>
                  <ul className="space-y-3 text-gray-400">
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Fonctionnalités</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Tarifs</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Réservations</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">API</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Tableau de bord</a></li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-5">Réseau</h4>
                  <ul className="space-y-3 text-gray-400">
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Carte des écrans</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Audience</a></li>
                    <li><a href="#partenariat" className="hover:text-[#E84E0E] transition-colors duration-200">Devenir partenaire</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Formats supportés</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Emplacements premium</a></li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-5">Ressources</h4>
                  <ul className="space-y-3 text-gray-400">
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Centre d'aide</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Blog</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Études de cas</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Galerie créative</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Tendances DOOH</a></li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-5">Entreprise</h4>
                  <ul className="space-y-3 text-gray-400">
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">À propos</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Équipe</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Carrières</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Presse</a></li>
                    <li><a href="#contact" className="hover:text-[#E84E0E] transition-colors duration-200">Contact</a></li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Section Newsletter */}
            <div className="mt-12 pt-8 border-t border-gray-800">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div className="mb-6 md:mb-0">
                  <h4 className="font-bold text-lg mb-2">Restez informé</h4>
                  <p className="text-gray-400">Recevez nos actualités et offres spéciales</p>
                </div>
                <div className="w-full md:w-auto">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0">
                    <input 
                      type="email" 
                      placeholder="Votre email" 
                      className="px-4 py-3 bg-gray-800 text-white rounded-lg sm:rounded-r-none focus:outline-none focus:ring-2 focus:ring-[#E84E0E] w-full md:w-64"
                    />
                    <button className="bg-[#E84E0E] hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-lg sm:rounded-l-none transition-colors duration-300">
                      S'abonner
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Section Copyright */}
        <div className="py-6 bg-black">
          <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-500 text-sm mb-4 md:mb-0">
              © 2025 Accent Media & Technology SA. Tous droits réservés.
            </div>
            <div className="flex flex-wrap justify-center space-x-6 text-sm">
              <a href="#" className="text-gray-500 hover:text-gray-400 transition-colors duration-200 mb-2 md:mb-0">Confidentialité</a>
              <a href="#" className="text-gray-500 hover:text-gray-400 transition-colors duration-200 mb-2 md:mb-0">Conditions d'utilisation</a>
              <a href="#" className="text-gray-500 hover:text-gray-400 transition-colors duration-200 mb-2 md:mb-0">Mentions légales</a>
              <a href="#" className="text-gray-500 hover:text-gray-400 transition-colors duration-200 mb-2 md:mb-0">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Bouton flottant de contact et retour en haut - Inspiré des UI modernes sur Dribbble */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col space-y-3">
        {/* Bouton de chat */}
        <motion.button 
          className="bg-[#E84E0E] text-white p-4 rounded-full shadow-lg flex items-center justify-center"
          whileHover={{ scale: 1.1, boxShadow: "0 10px 25px -5px rgba(232, 78, 14, 0.4)" }}
          whileTap={{ scale: 0.95 }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1, type: "spring", stiffness: 200, damping: 15 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </motion.button>
        
        {/* Bouton retour en haut */}
        <motion.a 
          href="#accueil"
          className="bg-white text-gray-800 p-3 rounded-full shadow-lg flex items-center justify-center border border-gray-200"
          whileHover={{ scale: 1.1, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
          whileTap={{ scale: 0.95 }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ 
            scale: scrollY > 300 ? 1 : 0,
            opacity: scrollY > 300 ? 1 : 0,
          }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </motion.a>
      </div>
    </div>
  );
};

// Composant pour l'icône de notification (utilisé dans l'interface)
const BellIcon = ({ className }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
};

// Composant pour l'icône de filtre (utilisé dans l'interface de carte)
const FilterIcon = ({ className }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
  );
};

// Composant pour l'icône de téléchargement (utilisé dans l'interface analytique)
const DownloadIcon = ({ className }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
};

// Composant pour l'icône de flèche vers le haut (utilisé dans les statistiques)
const ArrowUpIcon = ({ className }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
    </svg>
  );
};

export default LandingPage;